import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ToastController } from '@ionic/angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { StripePaymentElementComponent } from '../../stripe-payment-element/stripe-payment-element.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { ButtonComponent } from '../../ui/button/button.component';
import { CardComponent } from '../../ui/card/card.component';
import { PanelHostService, MoneyOutPaymentMethod } from '../../../services/panel-host.service';
import { MyInfoService } from '../../../services/my-info.service';
import { TripFinanceService } from '../../../services/trip-finance.service';
import { TripService } from '../../../services/trip.service';
import { buildStripeBillingDetails } from '../../../services/payment/participant-payment.utils';
import {
  buildTripMoneyOutPaymentConfig,
  StripePaymentMethodType,
} from '../../../models/stripe-payment.types';
import { presentPaymentSuccessToast } from '../../../services/payment/payment-page-toasts';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-money-out-payment-panel',
  imports: [
    FaIconComponent,
    StripePaymentElementComponent,
    InlineAlertComponent,
    ButtonComponent,
    CardComponent,
    TranslocoPipe,
    ContentContainerComponent,
    PanelBodyComponent,
    FontAwesomeModule
  ],
  templateUrl: './money-out-payment-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoneyOutPaymentPanelComponent {
  private readonly panelHostService = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly myInfoService = inject(MyInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly selectedMethod = signal<MoneyOutPaymentMethod | null>(null);
  readonly paymentSuccess = signal(false);

  readonly trip = computed(() => this.tripService.currentTrip.value());
  readonly summaryLoading = computed(() => this.tripFinanceService.financeSummary.isLoading());
  readonly activeUser = computed(() => this.myInfoService.activeUser());

  readonly fullPaymentAmountCents = computed(() => {
    const remaining = this.tripFinanceService.financeSummary.value()?.totalAmountRemaining;
    if (remaining === undefined || remaining <= 0) {
      return null;
    }
    return Math.round(remaining * 100);
  });

  readonly stripePaymentMethod = computed((): StripePaymentMethodType | null => {
    const method = this.selectedMethod();
    if (method === 'credit-card') {
      return 'card';
    }
    if (method === 'ach') {
      return 'us_bank_account';
    }
    return null;
  });

  readonly stripeConfig = computed(() => {
    const trip = this.trip();
    const method = this.stripePaymentMethod();
    if (!trip || !method) {
      return null;
    }
    return buildTripMoneyOutPaymentConfig(trip.id, String(trip.custId), method);
  });

  readonly billingDetails = computed(() => {
    const user = this.activeUser();
    if (!user) {
      return null;
    }
    return buildStripeBillingDetails(user);
  });

  readonly canShowStripeFlow = computed(
    () =>
      this.stripeConfig() !== null &&
      this.billingDetails() !== null &&
      this.stripePaymentMethod() !== null,
  );

  constructor() {
    effect(() => {
      const panel = this.panelHostService.activePanel();
      if (panel === 'money-out-payment') {
        const preselected = this.panelHostService.moneyOutPaymentMethod();
        if (preselected) {
          this.paymentSuccess.set(false);
          this.selectedMethod.set(preselected);
        }
        return;
      }

      this.selectedMethod.set(null);
      this.paymentSuccess.set(false);
    });
  }

  close(): void {
    this.panelHostService.dismiss();
  }

  selectMethod(method: MoneyOutPaymentMethod): void {
    this.paymentSuccess.set(false);
    this.selectedMethod.set(method);
  }

  clearMethod(): void {
    this.selectedMethod.set(null);
  }

  async onPaymentSucceeded(): Promise<void> {
    this.tripFinanceService.notifyFinanceChanged();
    this.selectedMethod.set(null);
    this.paymentSuccess.set(true);
    await presentPaymentSuccessToast(this.transloco, this.toastController);
  }
}
